import React, { useState, useEffect } from "react";
import axios from "axios";
import io from "socket.io-client";
import { useSelector } from "react-redux";
import { selectGeneralSettings } from "../../../app/settingsSlice";
import { useDbContext } from "../../../context/DbContext";
import {
  FiTrendingUp,
  FiSettings,
  FiRefreshCw,
} from "react-icons/fi";

const LiveRateCard = ({ onOpenRateSettings, className = "" }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const generalSettings = useSelector(selectGeneralSettings);

  const [liveRates, setLiveRates] = useState([]);
  const [isEnabled, setIsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  // 1. Check software settings for "SHOW LIVE RATES IN HOME PAGE"
  useEffect(() => {
    if (generalSettings && generalSettings.length > 0) {
      const setting = generalSettings.find(
        (s) =>
          s.Name === "SHOW LIVE RATES IN HOME PAGE" ||
          s.Name === "SHOW LIVE RATES IN HOME"
      );
      if (setting) {
        setIsEnabled(setting.ss_status === 1);
      }
    }
  }, [generalSettings]);

  // 2. Fetch live rate classes and their latest stored rates
  const fetchLiveRates = async () => {
    if (!dbCode) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${apiBaseUrl}/main/getRateClassesWithLatestRates/${dbCode}`
      );
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        setLiveRates(res.data);
      } else {
        const fallbackRes = await axios.get(
          `${apiBaseUrl}/main/selectall/${dbCode}`
        );
        if (fallbackRes.data && Array.isArray(fallbackRes.data)) {
          setLiveRates(fallbackRes.data);
        }
      }
    } catch (err) {
      console.error("Error fetching live rates for LiveRateCard:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveRates();
  }, [apiBaseUrl, dbCode]);

  // 3. Socket listener for real-time live rate updates
  useEffect(() => {
    if (!apiBaseUrl) return;
    const socket = io(apiBaseUrl);
    socket.on("rateUpdated", () => {
      fetchLiveRates();
    });
    socket.on("rateHistoryUpdated", () => {
      fetchLiveRates();
    });
    return () => socket.disconnect();
  }, [apiBaseUrl]);

  // Do not render if disabled in General Settings
  if (!isEnabled) {
    return null;
  }

  return (
    <div
      className={`w-full bg-blue-400/20 hover:bg-blue-400/30 border border-blue-300/30 backdrop-blur-xl rounded-2xl p-2.5 xl:p-3 2xl:p-4 shadow-xl transition-all duration-300 select-none flex flex-col gap-1.5 2xl:gap-3 font-inter ${className}`}
    >
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-1.5 2xl:pb-2.5 border-b border-white/20">
        <div className="flex items-center gap-1.5 2xl:gap-2.5">
          <div className="w-6 h-6 2xl:w-8 2xl:h-8 rounded-full border border-amber-300/40 bg-amber-400/20 flex items-center justify-center text-amber-300 shrink-0">
            <FiTrendingUp className="w-3 h-3 2xl:w-4 2xl:h-4" />
          </div>
          <div>
            <div className="flex items-center gap-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400"></span>
              </span>
              <span className="text-white font-extrabold text-[10.5px] 2xl:text-xs tracking-wide uppercase">
                Live Rates
              </span>
            </div>
            <p className="text-[9px] 2xl:text-[10px] text-blue-100/80 font-medium leading-none mt-0.5">
              Current Market Prices
            </p>
          </div>
        </div>

        {/* Action Control Icons */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={fetchLiveRates}
            className="w-5 h-5 2xl:w-7 2xl:h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors cursor-pointer"
            title="Refresh Rates"
          >
            <FiRefreshCw className={`w-2.5 h-2.5 2xl:w-3.5 2xl:h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          {onOpenRateSettings && (
            <button
              type="button"
              onClick={onOpenRateSettings}
              className="w-5 h-5 2xl:w-7 2xl:h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-amber-300 transition-colors cursor-pointer"
              title="Open Rate Settings"
            >
              <FiSettings className="w-2.5 h-2.5 2xl:w-3.5 2xl:h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Rates Cards List — Exactly 3 records visible, smooth invisible scrolling if more */}
      <div
        className="flex flex-col gap-1.5 2xl:gap-2 max-h-[108px] 2xl:max-h-[130px] overflow-y-auto no-scrollbar scroll-smooth pr-0"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {loading && liveRates.length === 0 ? (
          <div className="py-2 text-center text-blue-100/70 text-xs italic font-medium">
            Fetching rates...
          </div>
        ) : liveRates.length === 0 ? (
          <div className="py-2 text-center text-blue-100/70 text-xs font-medium">
            No rate classes registered
          </div>
        ) : (
          liveRates.map((rc, idx) => {
            const name = rc.rateClass || rc.name || "Gold";
            const rateVal = rc.currentRate || rc.rate || 0;
            const formattedRate = Number(rateVal).toLocaleString("en-IN");
            const is916 = name.toLowerCase().includes("916");
            const isSilver = name.toLowerCase().includes("silver");
            const is22k = name.toLowerCase().includes("22k");

            return (
              <div
                key={rc.id || idx}
                onClick={onOpenRateSettings}
                className={`flex items-center justify-between h-[32px] 2xl:h-[38px] px-2.5 rounded-lg 2xl:rounded-xl border cursor-pointer transition-all duration-200 hover:scale-[1.01] shadow-xs shrink-0 ${
                  is916 || is22k
                    ? "bg-amber-400/20 border-amber-300/40 text-white hover:bg-amber-400/30"
                    : isSilver
                    ? "bg-slate-200/20 border-slate-200/40 text-white hover:bg-slate-200/30"
                    : "bg-white/10 border-white/20 text-white hover:bg-white/20"
                }`}
                title="Click to open Rate Settings"
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      is916 || is22k
                        ? "bg-amber-400 shadow-xs shadow-amber-400"
                        : isSilver
                        ? "bg-slate-200 shadow-xs"
                        : "bg-blue-400"
                    }`}
                  ></span>
                  <span className="font-bold text-[11px] 2xl:text-xs tracking-wide text-white">
                    {name}
                  </span>
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="font-extrabold text-amber-300 text-xs 2xl:text-sm tracking-tight">
                    ₹ {formattedRate}
                  </span>
                  <span className="text-[9px] 2xl:text-[10px] text-blue-100/70 font-semibold">
                    /g
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LiveRateCard;
